import React, { useMemo, useState, useEffect } from "react";
import { Card, Statistic, Row, Col, Empty } from "antd";
import dayjs from "dayjs";
import {
  UI_DATE_FORMAT,
  BACKEND_DATE_FORMAT,
  COLORS,
} from "../../../../consts";
import {
  calculateRowLength,
  cleanGhName,
  isDisplayedLeafSizeWithinRange,
} from "./utils";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ChartTitle,
  ChartTooltip,
  ChartLegend,
);

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

/**
 * Superuser-only summary cards for recommendation compliance.
 */
const RecommendationCards = ({
  data, // summaryForecastData (map: greenhouseName -> rows[])
  selectedGreenhouses = [],
  siteMetadata = {},
  minLeafSize,
  maxLeafSize,
  greenhouseLabel = "Greenhouse",
  blockLabel = "Row",
  yieldPerMeterTitle = "Yield Per Meter",
}) => {
  // Same logic as the "Within Recommendation" table cell
  const isWithinRecommendation = (row) => {
    const harvest = row?.harvestDate ? dayjs(row.harvestDate) : null;
    if (!harvest || !harvest.isValid()) return null; // N/A

    const harvestBackend = harvest.format(BACKEND_DATE_FORMAT);
    const inRange = (v) =>
      isDisplayedLeafSizeWithinRange(v, minLeafSize, maxLeafSize);

    if (row?.lastData?.date) {
      const lastBackend = dayjs(row.lastData.date, UI_DATE_FORMAT).format(
        BACKEND_DATE_FORMAT,
      );
      if (lastBackend === harvestBackend) {
        return inRange(row.lastData.averagePercentile80_95);
      }
    }

    const current = row?.lastData?.averagePercentile80_95 || 0;
    for (const key of ["day1", "day2", "day3", "day4", "day5"]) {
      const d = row[key];
      if (d && typeof d === "object" && d.date) {
        const fBackend = dayjs(d.date, UI_DATE_FORMAT).format(
          BACKEND_DATE_FORMAT,
        );
        if (fBackend === harvestBackend) {
          const total = current + (d.averagePercentile80_95 || 0);
          return inRange(total);
        }
      }
    }
    return false;
  };

  const yieldPerTubs =
    siteMetadata?.FORECAST_GREENHOUSE_TABLE?.YIELD_PER_TUBS || 1;

  const computeYieldPerMeter = (ghName, row) => {
    const name = cleanGhName(ghName)
    const coords =
      siteMetadata?.GREENHOUSE_COORDINATES?.[name]?.[row.plotName];
    if (!coords || !Array.isArray(coords) || coords.length < 3) return null;
    if (!row?.yield || row.yield <= 0) return null;

    const length = calculateRowLength(coords);
    if (!length || length <= 0) return null;

    const totalYield = row.yield * yieldPerTubs; // kg
    return totalYield / length; // kg/m
  };

  const stats = useMemo(() => {
    const includedGH =
      selectedGreenhouses?.length > 0
        ? selectedGreenhouses
        : Object.keys(data || {});

    let followed = 0;
    let notFollowed = 0;

    const ypmFollowed = [];
    const ypmNotFollowed = [];

    includedGH.forEach((ghName) => {
      const rows = data?.[ghName] || [];
      rows.forEach((row) => {
        const status = isWithinRecommendation(row); // true/false/null
        if (status === null) return;

        if (status === true) followed += 1;
        else notFollowed += 1;

        const ypm = computeYieldPerMeter(ghName, row);
        if (ypm != null) {
          if (status === true) ypmFollowed.push(ypm);
          else ypmNotFollowed.push(ypm);
        }
      });
    });

    const avg = (arr) =>
      arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : 0;

    const avgFollowed = avg(ypmFollowed);
    const avgNot = avg(ypmNotFollowed);

    const improvementPct =
      avgNot > 0 ? ((avgFollowed - avgNot) / avgNot) * 100 : null;

    return {
      followed,
      notFollowed,
      avgFollowed,
      avgNot,
      improvementPct,
    };
  }, [data, selectedGreenhouses, siteMetadata, minLeafSize, maxLeafSize]);

  const baseChartSeries = useMemo(() => {
    const includedGH =
      selectedGreenhouses?.length > 0
        ? selectedGreenhouses
        : Object.keys(data || {});

    // key -> { label, f:[], n:[], ts }
    const buckets = new Map();

    includedGH.forEach((ghName) => {
      const rows = data?.[ghName] || [];
      rows.forEach((row) => {
        const status = isWithinRecommendation(row);
        if (status === null) return;

        const ypm = computeYieldPerMeter(ghName, row);
        if (ypm == null) return;

        const d = dayjs(row.harvestDate);
        if (!d.isValid()) return;

        const firstHalf = d.date() <= 15;
        const startDay = firstHalf ? 1 : 16;
        const endDay = firstHalf ? 15 : d.daysInMonth();

        const label = `${String(startDay).padStart(2, "0")}-${String(
          endDay,
        ).padStart(2, "0")} ${d.format("MMM YYYY")}`;

        const key = `${d.format("YYYY-MM")}-${startDay}`;
        const ts = d.date(startDay).valueOf();

        if (!buckets.has(key)) buckets.set(key, { label, f: [], n: [], ts });
        if (status === true) buckets.get(key).f.push(ypm);
        else buckets.get(key).n.push(ypm);
      });
    });

    const avg = (arr) =>
      arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : 0;

    const sorted = Array.from(buckets.entries()).sort(
      (a, b) => a[1].ts - b[1].ts,
    );

    const labels = sorted.map(([, v]) => v.label);
    const datasetFollowed = sorted.map(([, v]) => Number(avg(v.f).toFixed(2)));
    const datasetNot = sorted.map(([, v]) => Number(avg(v.n).toFixed(2)));

    return { labels, datasetFollowed, datasetNot };
  }, [data, selectedGreenhouses, siteMetadata, minLeafSize, maxLeafSize]);

  // Custom legend visibility (like DistributionGraph)
  const [visibility, setVisibility] = useState({
    Followed: true,
    "Not Followed": true,
  });

  useEffect(() => {
    // Ensure keys exist even if component remounts/props change
    setVisibility((prev) => ({
      Followed: prev.Followed ?? true,
      "Not Followed": prev["Not Followed"] ?? true,
    }));
  }, [baseChartSeries.labels.length]);

  const toggleLineVisibility = (label) => {
    setVisibility((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const pctNodeAndStyle = (() => {
    if (
      stats.improvementPct == null ||
      !Number.isFinite(stats.improvementPct)
    ) {
      return { node: "N/A", style: { color: COLORS.darkerGray } };
    }

    if (stats.improvementPct < 0) {
      return {
        node: stats.improvementPct.toFixed(1) + "%",
        style: { color: COLORS.dangerRed },
      };
    }

    const pctClamped = clamp(stats.improvementPct, 0, 100);
    const color =
      pctClamped <= 0
        ? COLORS.dangerRed
        : pctClamped < 10
          ? COLORS.primaryOrange
          : COLORS.successGreen;

    return { node: `${pctClamped.toFixed(1)}%`, style: { color } };
  })();

  const threeD =
    "rounded-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-100";

  const chartData = {
    labels: baseChartSeries.labels,
    datasets: [
      {
        label: "Followed",
        data: baseChartSeries.datasetFollowed,
        borderColor: COLORS.successGreen,
        backgroundColor: COLORS.successGreen,
        fill: false,
        tension: 0.3,
        pointRadius: 3,
        hidden: !visibility["Followed"],
        spanGaps: true,
      },
      {
        label: "Not Followed",
        data: baseChartSeries.datasetNot,
        borderColor: COLORS.primaryBlue,
        backgroundColor: COLORS.primaryBlue,
        fill: false,
        tension: 0.3,
        pointRadius: 3,
        hidden: !visibility["Not Followed"],
        spanGaps: true,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (tooltipItem) =>
            `${tooltipItem.dataset.label}: ${tooltipItem.raw} kg/m`,
        },
      },
      title: { display: false },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "15-day period",
          font: { size: 16 },
        },
        grid: { display: false },
        ticks: { autoSkip: false, font: { size: 14 } },
      },
      y: {
        title: {
          display: true,
          text: "Average " + yieldPerMeterTitle + " (kg/m)",
          font: { size: 16 },
        },
        grid: { display: false },
        beginAtZero: true,
        ticks: { font: { size: 14 } },
      },
    },
  };

  return (
    <div className="w-full my-6">
      <Row gutter={[16, 16]} style={{ alignItems: "stretch" }}>
        <Col xs={24} md={8} style={{ display: "flex" }}>
          <Card
            variant="outlined"
            className={`h-full w-full flex flex-col ${threeD}`}
          >
            <Statistic
              title={`Rows followed recommendation (${greenhouseLabel}s selected)`}
              value={stats.followed}
            />
            <div className="text-xs text-gray-500 mt-2">
              Count of {blockLabel}s where harvest aligned with ideal spec on
              the measured/forecasted date.
            </div>
          </Card>
        </Col>

        <Col xs={24} md={8} style={{ display: "flex" }}>
          <Card
            variant="outlined"
            className={`h-full w-full flex flex-col ${threeD}`}
          >
            <Statistic
              title={`Rows did NOT follow recommendation (${greenhouseLabel}s selected)`}
              value={stats.notFollowed}
            />
            <div className="text-xs text-gray-500 mt-2">
              Count of {blockLabel}s with harvest outside the ideal spec window.
            </div>
          </Card>
        </Col>

        <Col xs={24} md={8} style={{ display: "flex" }}>
          <Card
            variant="outlined"
            className={`h-full w-full flex flex-col ${threeD}`}
          >
            <Statistic
              title={"Percentage Growth in " + yieldPerMeterTitle}
              value={pctNodeAndStyle.node}
              styles={{ content: pctNodeAndStyle.style }}
            />
            <div
              className="mt-2 text-xs leading-5"
              style={{ color: COLORS.darkerGray }}
            >
              <div>
                Avg (Followed):{" "}
                {stats.avgFollowed
                  ? `${stats.avgFollowed.toFixed(2)} kg/m`
                  : "N/A"}
              </div>
              <div>
                Avg (Not):{" "}
                {stats.avgNot ? `${stats.avgNot.toFixed(2)} kg/m` : "N/A"}
              </div>
              <div className="mt-1">
                {stats.improvementPct == null
                  ? "Insufficient harvested rows with geometry to compare."
                  : "Estimated extra " +
                    yieldPerMeterTitle.toLowerCase() +
                    " per meter when recommendations are followed."}
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="mt-4">
        <Col xs={24}>
          <Card
            variant="outlined"
            className={`w-full ${threeD}`}
            title={
              "Average " +
              yieldPerMeterTitle +
              " (kg/m) by 15-day Period: Followed vs Not Followed"
            }
          >
            {/* Custom legend */}
            <div className="custom-legend flex flex-wrap gap-4 mb-3 sm:flex-col">
              {[
                { label: "Followed", color: COLORS.successGreen },
                { label: "Not Followed", color: COLORS.primaryBlue },
              ].map(({ label, color }) => (
                <div
                  key={label}
                  className="flex items-center mb-1 cursor-pointer"
                  onClick={() => toggleLineVisibility(label)}
                  title={`Toggle ${label}`}
                >
                  <div
                    className="relative w-10 h-0.5 mr-2"
                    style={{
                      backgroundColor: color,
                      opacity: visibility[label] ? 1 : 0.3,
                    }}
                  >
                    <div
                      className="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full"
                      style={{
                        backgroundColor: color,
                        opacity: visibility[label] ? 1 : 0.3,
                      }}
                    />
                  </div>
                  <span style={{ opacity: visibility[label] ? 1 : 0.3 }}>
                    {label}
                  </span>
                </div>
              ))}
            </div>

            {baseChartSeries.labels.length === 0 ? (
              <Empty description="No harvested rows with geometry found for the selected bays." />
            ) : (
              <div style={{ width: "100%", height: 320 }}>
                <Line data={chartData} options={chartOptions} />
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default RecommendationCards;
